<script setup lang="ts">
import { computed, provide, watch } from 'vue'
import { useForm } from 'vee-validate'
import FieldToggle from '@/components/shared/Fields/FieldToggle.vue'
import Tooltip from '@/components/shared/Tooltip.vue'
import { useCampaignPolicies } from '@/composables/useCampaignPolicies'
import {
  useNotificationChannelsQuery,
  useUpdateNotificationChannelsMutation,
} from '@/composables/useNotifications'

const {
  data: channelsData,
  refetch: refetchChannels,
  isLoading: isLoadingChannels,
} = useNotificationChannelsQuery()
const { mutate: updateChannels, isLoading: isUpdating } = useUpdateNotificationChannelsMutation()
const {
  data: campaignPolicies,
  isLoading: isLoadingPolicies,
  refetch: refetchCampaignPolicies,
} = useCampaignPolicies()

// Get all available channels from campaign policies
const availableChannels = computed(() => {
  if (!campaignPolicies.value?.AvailableNotificationChannels) return []

  return campaignPolicies.value.AvailableNotificationChannels.filter(
    (channel) => channel.Name !== 'Service Panel Notifications',
  )
})

const isLoading = computed(() => isLoadingChannels.value || isLoadingPolicies.value)

// Create stable initial values based on user data
const getInitialValues = () => {
  if (!channelsData.value?.Data) return {}
  const vals: Record<string, boolean> = {}
  channelsData.value.Data.forEach((c) => {
    vals[c.Name] = c.Enabled
  })
  return vals
}

const { setValues, values } = useForm({
  initialValues: getInitialValues(),
})

provide('formValues', values)

// Fast lookup set of available channel names
const availableChannelNameSet = computed(() => {
  return new Set(availableChannels.value.map((c) => c.Name))
})

const enabledCount = computed(() => {
  const allowed = availableChannelNameSet.value
  return Object.entries(values).filter(([name, v]) => allowed.has(name) && Boolean(v)).length
})

const getChannelChecked = (channelName: string) => {
  return values[channelName] ?? false
}

const serverEnabledNames = computed(() => {
  if (!channelsData.value?.Data) return [] as string[]
  return channelsData.value.Data.filter(
    (c) => c.Enabled && availableChannelNameSet.value.has(c.Name),
  )
    .map((c) => c.Name)
    .sort()
})

// Determine which channels should be disabled (prevent deselecting the last enabled channel)
const disabledChannels = computed(() => {
  if (!availableChannels.value?.length) return new Set<string>()

  const disabled = new Set<string>()

  // If only one channel is enabled, disable that channel to prevent deselecting it
  if (enabledCount.value <= 1) {
    availableChannels.value.forEach((channel) => {
      if (getChannelChecked(channel.Name)) {
        disabled.add(channel.Name)
      }
    })
  }

  return disabled
})

// Watch for changes in user channel data to reset form
watch(
  () => channelsData.value,
  (newData, oldData) => {
    // Only reset if we have new data and it's different from old data
    if (newData?.Data && (!oldData?.Data || newData.Data !== oldData.Data)) {
      const nextVals: Record<string, boolean> = {}
      const allowed = availableChannelNameSet.value
      newData.Data.forEach((c) => {
        if (allowed.has(c.Name)) nextVals[c.Name] = c.Enabled
      })
      setValues(nextVals, false)
    }
  },
  { immediate: true },
)

// Auto-save when values change
watch(
  values,
  (newValues) => {
    // Prevent auto-save if no user data loaded yet or currently updating
    if (!channelsData.value?.Data || isUpdating.value) return

    const enabled = Object.entries(newValues)
      .filter(([name, v]) => availableChannelNameSet.value.has(name) && v)
      .map(([name]) => name)
      .sort()

    // Prevent saving if no channels would be enabled (at least one must be selected)
    if (enabled.length === 0) {
      return
    }

    // Skip mutation if values match server state (initial sync or no-op changes)
    const serverEnabled = serverEnabledNames.value
    const isSameLength = enabled.length === serverEnabled.length
    const isSame = isSameLength && enabled.every((n, i) => n === serverEnabled[i])
    if (isSame) return

    updateChannels(enabled, {
      onSuccess: () => {
        // Refresh user channels and campaign policies after update
        refetchChannels()
        refetchCampaignPolicies()
      },
    })
  },
  { deep: true },
)
</script>

<template>
  <div class="px-3 py-2 max-w-80">
    <div class="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
      Notification Settings
    </div>

    <div>
      <div v-if="isLoading" class="overflow-y-auto pr-1 -mx-1 max-h-48">
        <div
          v-for="i in 2"
          :key="i"
          class="flex gap-2 justify-between items-center px-1 py-1 animate-pulse"
        >
          <div class="flex-1 min-w-0">
            <div class="w-28 h-3 bg-gray-200 rounded dark:bg-gray-700" />
            <div class="mt-1 w-40 h-2 bg-gray-200 rounded dark:bg-gray-700" />
          </div>
          <div class="flex items-center ml-4 shrink-0">
            <div class="w-9 h-5 bg-gray-200 rounded-full dark:bg-gray-700" />
          </div>
        </div>
      </div>
      <div v-else-if="availableChannels?.length" class="overflow-y-auto pr-1 -mx-1 max-h-48">
        <div
          v-for="channel in availableChannels"
          :key="channel.Id"
          class="flex gap-2 justify-between items-center px-1 py-1"
        >
          <div class="flex-1 min-w-0">
            <div class="text-[12px] font-medium text-gray-900 dark:text-gray-100 truncate">
              {{ channel.Name }}
            </div>
            <div v-if="channel.Description" class="text-[11px] text-gray-600 dark:text-gray-300">
              {{ channel.Description }}
            </div>
          </div>
          <div class="flex items-center ml-4 shrink-0">
            <Tooltip
              :disabled="!disabledChannels.has(channel.Name)"
              label="At least one notification channel must be enabled"
              side="top"
            >
              <FieldToggle
                :name="channel.Name"
                :default-checked="getChannelChecked(channel.Name)"
                :show-label="false"
                :validate="false"
                size="sm"
                :disabled="disabledChannels.has(channel.Name)"
              />
            </Tooltip>
          </div>
        </div>
      </div>
      <div v-else class="text-[11px] text-gray-500 dark:text-gray-400">
        No notification channels available.
      </div>
    </div>
  </div>
</template>
